import { Machine, MachineConfig, MachineOptions, sendParent } from 'xstate';
import {
  DeleteScreenshotDocument,
  GetScreenshotsDocument,
  ModelScreenshot,
} from 'src/generated/graphql';
import gqlClient from 'src/apolloClient';

export interface GalleryContext {
  jobId: number;
}

interface GallerySchema {
  context: GalleryContext;
  states: {
    idle: {};
    deleting: {};
    error: {};
  };
}

export type GalleryEvent =
  | { type: 'SHOW_LIGHTBOX'; id: string; isEnhanced: boolean }
  | { type: 'DELETE_SCREENSHOT'; id: string };

export const config: MachineConfig<
  GalleryContext,
  GallerySchema,
  GalleryEvent
> = {
  id: 'gallery',
  initial: 'idle',
  context: {
    jobId: 0,
  },
  on: {
    DELETE_SCREENSHOT: '.deleting',
    SHOW_LIGHTBOX: {
      actions: 'sendEventToParent',
    },
  },
  states: {
    idle: {},
    deleting: {
      entry: 'removeScreenshotFromCache',
      invoke: {
        src: 'deleteScreenshot',
        onDone: 'idle',
        onError: 'error',
      },
    },
    error: {},
  },
};

export const options: Partial<MachineOptions<GalleryContext, GalleryEvent>> = {
  actions: {
    sendEventToParent: sendParent(
      (_: GalleryContext, event: GalleryEvent) => event,
    ),
    removeScreenshotFromCache: async (context, event) => {
      const data: any = gqlClient.readQuery({
        query: GetScreenshotsDocument,
        variables: { jobId: context.jobId },
      });
      const screenshots = data.screenshots.filter(
        (s: ModelScreenshot) => s.id !== event.id,
      );
      await gqlClient.writeQuery({
        query: GetScreenshotsDocument,
        variables: { jobId: context.jobId },
        data: { screenshots },
      });
    },
  },
  services: {
    deleteScreenshot: async (context, event) =>
      gqlClient.mutate({
        mutation: DeleteScreenshotDocument,
        variables: { jobId: context.jobId, id: event.id },
      }),
  },
};

const galleryMachine = Machine(config, options);
export const generateGalleryMachine = (jobId: number) => {
  return galleryMachine.withContext({ jobId });
};

export default galleryMachine;
